import { useState } from "react";

import {
  Receipt,
  RECEIPT_STATUS_CANCELLATION_FAILED,
  RECEIPT_STATUS_CANCELLATION_REQUESTED,
  RECEIPT_STATUS_CANCELLED,
  RECEIPT_STATUS_DRAFT,
  RECEIPT_STATUS_DRAFT_DISCARDED,
  RECEIPT_STATUS_GENERATED,
  RECEIPT_STATUS_ERROR,
  formatReceiptNumber,
} from "../../models/receipt";
import {
  CButton,
  CButtonGroup,
  CCard,
  CCardBody,
  CCardHeader,
  CCollapse,
  CTooltip,
} from "@coreui/react";
import { formatUTCDateTime } from "../../utils/dates";
import { formatToCurrency } from "../../currency/format";
import { CURRENCY_CHOICES } from "../../currency/available-currencies";
import ReceiptCrudStatus from "./ReceiptCrudStatus";
import ReceiptCrudActions from "./ReceiptCrudActions";
import PermissionRequiredComponent from "../permissions/PermissionRequiredComponent";
import DocumentPrintButton from "../documents/DocumentPrintButton";
import { RECEIPTS_RETRIEVE } from "../../auth/permissions";

interface ReceiptCardProps {
  receipt: Receipt;
  onPDFClick: (inv: Receipt) => void;
  onCancellationClick: (inv: Receipt) => void;
  onReadClick: (inv: Receipt) => void;
  onUpdateClick: (inv: Receipt) => void;
  onDeleteClick: (inv: Receipt) => void;
  onHistoryClick: (inv: Receipt) => void;
  onConfirmDraftClick: (inv: Receipt) => void;
  onDiscardDraftClick: (inv: Receipt) => void;
  onSendEmailClick: (inv: Receipt) => void;
  onRegenerateKudeClick: (inv: Receipt) => void;
}

export function ReceiptCard({
  receipt,
  onPDFClick,
  onCancellationClick,
  onReadClick,
  onUpdateClick,
  onDeleteClick,
  onHistoryClick,
  onConfirmDraftClick,
  onDiscardDraftClick,
  onSendEmailClick,
  onRegenerateKudeClick,
}: ReceiptCardProps) {
  const [open, setOpen] = useState(true);

  const getStatusColor = (status?: string): string => {
    switch (status) {
      case RECEIPT_STATUS_DRAFT:
        return "secondary";
      case RECEIPT_STATUS_DRAFT_DISCARDED:
        return "dark";
      case RECEIPT_STATUS_GENERATED:
        return "success";
      case RECEIPT_STATUS_ERROR:
        return "danger";
      case RECEIPT_STATUS_CANCELLATION_REQUESTED:
        return "light";
      case RECEIPT_STATUS_CANCELLED:
        return "info";
      case RECEIPT_STATUS_CANCELLATION_FAILED:
        return "danger";
      default:
        return "info";
    }
  };

  let xmlButton = null;
  let pdfButton = null;

  if (receipt.pdfPath !== undefined) {
    pdfButton = (
      <PermissionRequiredComponent permissionName={RECEIPTS_RETRIEVE}>
        <CTooltip content="PDF">
          <CButton
            className="text-white"
            color="danger"
            onClick={() => {
              onPDFClick(receipt);
            }}
          >
            <i className="fa fa-file-pdf"></i>
          </CButton>
        </CTooltip>
      </PermissionRequiredComponent>
    );
  }

  return (
    <CCard className="mb-2">
      <CCardHeader
        className="d-flex justify-content-between align-items-center cursor-pointer"
        onClick={() => setOpen(!open)}
        color={getStatusColor(receipt.status)}
      >
        <span>
          <strong>{formatReceiptNumber(receipt)}</strong>
        </span>
        <span className="d-flex align-items-center gap-2">
          <strong>{formatUTCDateTime(new Date(receipt.receiptDate!))}</strong>
          <span
            className="rounded-circle bg-white d-flex justify-content-center align-items-center ml-1"
            style={{ width: "22px", height: "22px" }}
          >
            <i
              className={`fa fa-chevron-${open ? "up" : "down"}`}
              style={{ color: "black", fontSize: "12px" }}
            />
          </span>
        </span>
      </CCardHeader>
      <CCollapse show={open}>
        <CCardBody>
          <p>
            <strong>Razón Social:</strong> {receipt.clientSocialName}
          </p>
          <p>
            <strong>RUC:</strong> {receipt.clientRuc}
          </p>
          <p>
            <strong>Monto:</strong>{" "}
            {formatToCurrency(receipt.amount!, receipt.currency!)}
          </p>
          <p>
            <strong>Moneda:</strong> {CURRENCY_CHOICES.get(receipt.currency!)}
          </p>
          <p>
            <strong>Timbrado Usado:</strong> {receipt.stampIdentifier}
          </p>

          <p>
            <strong>Estado:</strong> <ReceiptCrudStatus receipt={receipt} />
          </p>

          <div>
            <strong>Archivos:</strong>{" "}
            <td className="text-center">
              <CButtonGroup>
                {xmlButton}
                {pdfButton}
                {receipt.pdfPath && receipt.id !== undefined && <DocumentPrintButton kind="receipt" documentId={receipt.id} />}
              </CButtonGroup>
            </td>
          </div>
          <div>
            <strong>Acciones disponibles:</strong>{" "}
            <ReceiptCrudActions
              receipt={receipt}
              onCancellationClick={onCancellationClick}
              onReadClick={onReadClick}
              onUpdateClick={onUpdateClick}
              onDeleteClick={onDeleteClick}
              onHistoryClick={onHistoryClick}
              onConfirmDraftClick={onConfirmDraftClick}
              onDiscardDraftClick={onDiscardDraftClick}
              onSendEmailClick={onSendEmailClick}
              onRegenerateKudeClick={onRegenerateKudeClick}
            />
          </div>
        </CCardBody>
      </CCollapse>
    </CCard>
  );
}
